import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './TeamMemberDashboard.css';
import TrainingViewer from './TrainingViewer';
import { downloadRecordedCertificate } from '../utils/certificateGenerator';

export function TeamMemberDashboard({ userId, userEmail }) {
  const { t } = useTranslation();
  const [trainings, setTrainings] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [viewingTraining, setViewingTraining] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadMemberData();
  }, [userId]);

  const loadMemberData = async () => {
    try {
      // Fetch user info
      if (userId) {
        try {
          const userRes = await fetch(`/api/training/user-info?userId=${userId}`);
          if (userRes.ok) {
            const userData = await userRes.json();
            setUser(userData);
          }
        } catch (err) {
          console.log('Failed to fetch user info:', err);
        }
      }

      const [trainingsRes, progressRes] = await Promise.all([
        fetch(`/api/training/member-trainings?userId=${userId}`),
        fetch(`/api/training/member-progress?userId=${userId}`)
      ]);

      if (!trainingsRes.ok || !progressRes.ok) throw new Error('Failed to load data');

      const trainingsData = await trainingsRes.json();
      const progressData = await progressRes.json();

      setTrainings(trainingsData);
      setProgress(progressData);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (training, newStatus, result = {}) => {
    setUpdatingId(`${training.module_id}-${training.team_id}`);
    try {
      console.log('[Training] Updating status:', {
        userId,
        moduleId: training.module_id,
        teamId: training.team_id,
        status: newStatus
      });

      const response = await fetch('/api/training/update-training-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          moduleId: training.module_id,
          teamId: training.team_id,
          status: newStatus,
          quizScore: result.quizScore
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update status');
      }

      const result = await response.json();
      console.log('[Training] Status updated successfully:', result);

      // Add a small delay to ensure database is updated before fetching
      setTimeout(() => {
        loadMemberData();
      }, 500);
    } catch (err) {
      console.error('[Training] Error updating status:', err);
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRestartTraining = async (training) => {
    if (window.confirm(`Are you sure you want to restart "${training.module_title}"? This will reset your progress.`)) {
      setUpdatingId(`${training.module_id}-${training.team_id}`);
      try {
        const response = await fetch('/api/training/update-training-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            moduleId: training.module_id,
            teamId: training.team_id,
            status: 'pending'
          })
        });

        if (!response.ok) throw new Error('Failed to restart training');
        loadMemberData();
      } catch (err) {
        setError(err.message);
      } finally {
        setUpdatingId(null);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'in-progress': return '#3b82f6';
      case 'overdue': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': t('manager.status.notStarted'),
      'in-progress': t('manager.status.inProgress'),
      'completed': t('manager.status.completed'),
      'overdue': 'Overdue'
    };
    return labels[status] || status;
  };

  const groupedByTeam = trainings.reduce((acc, training) => {
    const teamKey = training.team_name || 'Unassigned';
    if (!acc[teamKey]) {
      acc[teamKey] = [];
    }
    acc[teamKey].push(training);
    return acc;
  }, {});

  if (loading) {
    return <div className="member-loading">{t('common.loading')}</div>;
  }

  return (
    <div className="member-container">
      <div className="member-header">
        <h1>{t('teamMember.title')}</h1>
        <p>{t('teamMember.subtitle')}</p>
      </div>

      {error && <div className="member-error">{error}</div>}

      {/* Progress Overview */}
      {progress && (
        <section className="progress-overview">
          <div className="progress-card">
            <div className="progress-stat">
              <div className="stat-value">{progress.total_assigned}</div>
              <div className="stat-label">{t('manager.tableHeaders.assigned')}</div>
            </div>
          </div>

          <div className="progress-card">
            <div className="progress-stat completed">
              <div className="stat-value">{progress.completed}</div>
              <div className="stat-label">{t('manager.tableHeaders.completed')}</div>
            </div>
          </div>

          <div className="progress-card">
            <div className="progress-stat warning">
              <div className="stat-value">{progress.in_progress}</div>
              <div className="stat-label">{t('manager.status.inProgress')}</div>
            </div>
          </div>

          <div className="progress-card">
            <div className="progress-stat danger">
              <div className="stat-value">{progress.overdue}</div>
              <div className="stat-label">Overdue</div>
            </div>
          </div>

          <div className="progress-card full-width">
            <div className="progress-bar-container">
              <div className="progress-bar-label">
                <span>Overall Progress</span>
                <span className="percentage">{Math.round(progress.completion_percentage)}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress.completion_percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Trainings by Team */}
      {Object.keys(groupedByTeam).length === 0 ? (
        <div className="no-trainings">
          <p>No training assignments yet. Check back soon!</p>
        </div>
      ) : (
        <section className="trainings-section">
          {Object.entries(groupedByTeam).map(([teamName, teamTrainings]) => (
            <div key={teamName} className="team-group">
              <h2 className="team-name">{teamName}</h2>

              <div className="trainings-grid">
                {teamTrainings.map(training => (
                  <div
                    key={`${training.module_id}-${training.team_id}`}
                    className="training-card"
                    onClick={() => training.status !== 'completed' && setViewingTraining(training)}
                    style={{
                      borderLeftColor: getStatusColor(training.status),
                      cursor: training.status !== 'completed' ? 'pointer' : 'default'
                    }}
                  >
                    <div className="training-header">
                      <h3>{training.module_title}</h3>
                      <span
                        className="status-badge"
                        style={{
                          background: getStatusColor(training.status),
                          color: 'white'
                        }}
                      >
                        {getStatusLabel(training.status)}
                      </span>
                    </div>

                    <p className="training-description">
                      {training.module_description || 'No description available'}
                    </p>

                    {training.duration_minutes > 0 && (
                      <div className="training-meta">
                        <span>⏱️ {training.duration_minutes} minutes</span>
                      </div>
                    )}

                    <div className="training-dates">
                      <div className="date">
                        <small>Assigned</small>
                        <span>{new Date(training.assigned_at).toLocaleDateString()}</span>
                      </div>
                      <div className="date">
                        <small>Due</small>
                        <span className={new Date(training.due_date) < new Date() && training.status !== 'completed' ? 'overdue' : ''}>
                          {new Date(training.due_date).toLocaleDateString()}
                        </span>
                      </div>
                      {training.completed_at && (
                        <div className="date">
                          <small>Completed</small>
                          <span className="completed">
                            {new Date(training.completed_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {training.status !== 'completed' && (
                      <div className="training-actions">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (training.status === 'pending') handleStatusUpdate(training, 'in-progress');
                            setViewingTraining(training);
                          }}
                          className="btn btn-start"
                          disabled={updatingId === `${training.module_id}-${training.team_id}`}
                        >
                          {training.status === 'pending' ? t('teamMember.startTraining') : t('player.resumeLesson')}
                        </button>
                      </div>
                    )}

                    {training.status === 'completed' && (
                      <div className="training-complete-section">
                        <div className="training-complete-message">
                          ✓ Completed on {new Date(training.completed_at).toLocaleDateString()}
                        </div>
                        <div className="training-complete-actions">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const userName = user?.firstName && user?.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : user?.email || 'User';
                              downloadRecordedCertificate({
                                moduleId: training.module_id,
                                title: training.module_title,
                                completedAt: training.completed_at,
                                fallbackName: localStorage.getItem(`training_name_${userId}`) || userName,
                              });
                            }}
                            className="btn btn-certificate"
                            title="Download Certificate"
                          >
                            📜 Download Certificate
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestartTraining(training);
                            }}
                            className="btn btn-restart"
                            disabled={updatingId === `${training.module_id}-${training.team_id}`}
                          >
                            {updatingId === `${training.module_id}-${training.team_id}` ? '...' : '🔄 Retake'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {viewingTraining && (
        <TrainingViewer
          training={viewingTraining}
          userId={userId}
          onClose={() => setViewingTraining(null)}
          defaultCertificateName={localStorage.getItem(`training_name_${userId}`) || ''}
          onComplete={async (result) => {
            await handleStatusUpdate(viewingTraining, 'completed', result || {});
            if (result?.certificateName) localStorage.setItem(`training_name_${userId}`, result.certificateName);
            fetch('/api/training/create-training-certificate', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ moduleId: viewingTraining.module_id, certificateName: result?.certificateName })
            }).then(r => r.ok && r.json()).then(cert => {
              if (cert?.code) localStorage.setItem(`training_cert_${userId}_${viewingTraining.module_id}`, JSON.stringify(cert));
            }).catch(() => null);
            setViewingTraining(null);
          }}
        />
      )}

    </div>
  );
}

export default TeamMemberDashboard;
